import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, ChevronDown, Warehouse, Pin, PinOff } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useAuthStore } from '../../lib/stores/authStore';
import { usePinnedWarehouseItems } from './usePinnedWarehouseItems';
import type { WarehouseSection } from '../../lib/types/manufacturing';
import { SIDEBAR_GROUPS, buildResolvedGroups } from './warehouseMenuConfig';
import type { SidebarGroup, WarehouseMenuConfig } from './warehouseMenuConfig';
import { menuBadge, strongestDot } from '../../lib/commerce/menuRules';
import type { MenuSignals } from '../../lib/commerce/overview';
import { useWarehouseMenuCommerce } from '../../lib/commerce/useCommerceMenuSignals';
import { MenuDot, MenuSignalBadge } from './ecommerce/shared/MenuSignalBadge';


// ============================================
// TYPES
// ============================================

interface WarehouseSidebarProps {
  activeSection: WarehouseSection;
  onSectionChange: (section: WarehouseSection) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onClose?: () => void;
  isMobile?: boolean;
  onExitWarehouseMode?: () => void;
}

const PINNED_GROUP_KEY = 'pinned';

// ============================================
// ICONA HEADER
// ============================================

// Icona Magazzino con la stessa sfumatura rosa→arancio delle etichette dei
// gruppi. Lucide disegna a stroke, quindi serve un <linearGradient> SVG e
// stroke="url(#...)" (stesso trucco di menuIconGradient in Layout.tsx).
function WarehouseGradientIcon({ className }: { className?: string }) {
  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
        <defs>
          <linearGradient id="warehouseIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E91E8C" />
            <stop offset="100%" stopColor="#F97316" />
          </linearGradient>
        </defs>
      </svg>
      <Warehouse className={className} style={{ stroke: 'url(#warehouseIconGradient)' }} />
    </>
  );
}

// ============================================
// COLLAPSED SIDEBAR
// ============================================

function CollapsedSidebar({
  activeSection,
  onSectionChange,
  onToggleCollapse,
  filteredGroups,
  signals,
}: {
  activeSection: WarehouseSection;
  onSectionChange: (section: WarehouseSection) => void;
  onToggleCollapse?: () => void;
  filteredGroups: SidebarGroup[];
  signals: MenuSignals;
}) {
  const { isDark } = useTheme();

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      {/* Stesso pattern del menu CRM collassato (Layout.tsx): il logo resta
          centrato, il chevron "espandi" e' absolute sul bordo destro così non
          spinge il logo a sinistra. */}
      <div className="relative flex items-center justify-center h-12 px-1">
        <WarehouseGradientIcon className="w-5 h-5" />
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center justify-center w-4 h-6 rounded-md bg-white/80 backdrop-blur-[2px] shadow-sm opacity-70 hover:opacity-100 hover:bg-white transition-opacity"
            style={{ color: '#000' }}
            title="Espandi menu"
            aria-label="Espandi menu"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Icons only — scrollbar-hide: la barra di scorrimento ruberebbe
          larghezza alla colonna spostando le icone fuori asse col logo */}
      <div className="flex-1 overflow-y-auto scrollbar-hide px-1.5 py-1 space-y-0.5">
        {filteredGroups.flatMap(group =>
          group.items.map(item => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onSectionChange(item.id)}
                className={`menu-item warehouse-menu-item relative w-full flex items-center justify-center px-2 py-1.5 rounded-md transition-colors ${
                  isActive
                    ? 'menu-active'
                    : isDark
                      ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                      : ''
                }`}
                title={item.label}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={1.25} />
                {/* Menu collassato: il numero dei conflitti diventa un pallino ambra sull'icona (08 sez. 4) */}
                <MenuDot tone={strongestDot([menuBadge(item.badgeKey, signals)])} overlay />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function WarehouseSidebar({
  activeSection,
  onSectionChange,
  isCollapsed = false,
  onToggleCollapse,
  onClose,
  isMobile = false,
  onExitWarehouseMode,
}: WarehouseSidebarProps) {
  const { isDark } = useTheme();
  const { settings } = useSettingsStore();

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('warehouse-expanded-groups');
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch { return new Set(SIDEBAR_GROUPS.map(g => g.groupKey)); }
    }
    return new Set(SIDEBAR_GROUPS.map(g => g.groupKey));
  });

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      try { localStorage.setItem('warehouse-expanded-groups', JSON.stringify(Array.from(next))); } catch { /* memoria del browser non disponibile */ }
      return next;
    });
  };

  // Costruisce i gruppi con layout custom, label custom e voci nascoste
  const hiddenSections = (settings?.warehouse_hidden_sections as string[]) || [];
  const customLabels = (settings?.warehouse_menu_labels as Record<string, string>) || {};
  const menuConfig = settings?.warehouse_menu_config as WarehouseMenuConfig | undefined;

  // Contesto e-commerce: senza negozi (motore spento) e' quello di prima, menu identico (menuRules.ts)
  const { ctx: commerceCtx, signals } = useWarehouseMenuCommerce();
  const resolvedGroups = buildResolvedGroups(menuConfig, hiddenSections, customLabels, commerceCtx);

  // Voci fissate dall'utente: salite in cima in un gruppo "Fissate" e tolte dal gruppo di origine
  const userId = useAuthStore(s => s.user?.id);
  const { pinned, togglePin } = usePinnedWarehouseItems(userId);
  const pinnedItems = pinned
    .map(id => resolvedGroups.flatMap(g => g.items).find(i => i.id === id))
    .filter((i): i is SidebarGroup['items'][number] => !!i);
  const filteredGroups: SidebarGroup[] = pinnedItems.length > 0
    ? [
        { groupKey: PINNED_GROUP_KEY, label: 'Fissate', items: pinnedItems },
        ...resolvedGroups
          .map(g => ({ ...g, items: g.items.filter(i => !pinned.includes(i.id)) }))
          .filter(g => g.items.length > 0),
      ]
    : resolvedGroups;

  // Quando siamo su una sub-route (es. /magazzino/prodotti/:id),
  // MagazzinoMain non è montato → dobbiamo navigare a /magazzino?section=...
  const navigate = useNavigate();
  const location = useLocation();
  const handleSectionChange = useCallback((section: WarehouseSection) => {
    onSectionChange(section);
    const isSubRoute = location.pathname !== '/magazzino' && location.pathname !== '/magazzino/';
    if (isSubRoute) {
      const search = section !== 'dashboard' ? `?section=${section}` : '';
      navigate(`/magazzino${search}`);
    }
  }, [onSectionChange, location.pathname, navigate]);

  if (isCollapsed && !isMobile) {
    return (
      <CollapsedSidebar
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        onToggleCollapse={onToggleCollapse}
        filteredGroups={filteredGroups}
        signals={signals}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      {/* h-12 = stessa altezza del DynamicHeader, così i due border-b sono allineati */}
      <div className={`flex items-center justify-between h-12 px-4 border-b ${
        isDark ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <div className="flex items-center gap-2 min-w-0">
          <WarehouseGradientIcon className="w-5 h-5 flex-shrink-0" />
          <span className={`font-semibold text-sm truncate ${
            isDark ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Magazzino
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isMobile && onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className={`p-1.5 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
              }`}
              title="Comprimi menu"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          {isMobile && onClose && (
            <button
              onClick={onClose}
              className={`p-1.5 rounded-lg transition-colors ${
                isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {filteredGroups.map((group) => (
          <div key={group.groupKey}>
            {/* Separatore visivo tra magazzino base e produzione */}
            {group.separatorBefore && (
              <div className={`flex items-center gap-2 px-2 pt-3 pb-1 mt-1 mb-1`}>
                <div className={`flex-1 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <span className={`text-[9px] font-bold uppercase tracking-widest whitespace-nowrap ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {group.separatorBefore}
                </span>
                <div className={`flex-1 h-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
              </div>
            )}
            <div className="mb-2 mt-4 first:mt-0">
            <button
              type="button"
              onClick={() => { if (group.groupKey !== PINNED_GROUP_KEY) toggleGroup(group.groupKey); }}
              className="menu-section-label w-full flex items-center gap-1 px-3 py-1.5 text-xs font-medium uppercase tracking-wider hover:bg-accent/50 rounded-md transition-colors"
            >
              {group.groupKey === PINNED_GROUP_KEY ? (
                <Pin className="h-2.5 w-2.5 text-[#E91E8C]" />
              ) : (
                <ChevronDown
                  className={`h-2.5 w-2.5 text-[#E91E8C] transition-transform duration-150 ${expandedGroups.has(group.groupKey) ? '' : '-rotate-90'}`}
                />
              )}
              <span className="bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent">
                {group.label}
              </span>
              {group.groupKey !== PINNED_GROUP_KEY && !expandedGroups.has(group.groupKey) && (
                <MenuDot tone={strongestDot(group.items.map(i => menuBadge(i.badgeKey, signals)))} />
              )}
            </button>
            {(group.groupKey === PINNED_GROUP_KEY || expandedGroups.has(group.groupKey)) && (
            <div className="mt-0.5 space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                const isPinned = pinned.includes(item.id);
                return (
                  <div key={item.id} className="relative group">
                  <button
                    onClick={() => {
                      handleSectionChange(item.id);
                      if (isMobile && onClose) onClose();
                    }}
                    className={`menu-item warehouse-menu-item w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                      isActive
                        ? 'menu-active'
                        : `${isDark
                          ? 'text-gray-300 hover:bg-gray-700/50 hover:text-gray-100'
                          : ''}`
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                    <MenuSignalBadge badge={menuBadge(item.badgeKey, signals)} />
                    {item.badge && (
                      <span className={`ml-auto flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                        isDark
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                    {isActive && (
                      <span
                        className="ml-auto inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #E91E8C, #F97316)' }}
                      />
                    )}
                  </button>
                  <span
                    role="button"
                    tabIndex={0}
                    title={isPinned ? 'Togli dalle fissate' : 'Fissa in alto'}
                    aria-label={isPinned ? `Togli ${item.label} dalle fissate` : `Fissa ${item.label} in alto`}
                    onClick={(e) => { e.stopPropagation(); togglePin(item.id); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); togglePin(item.id); } }}
                    className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md cursor-pointer transition-opacity focus-visible:opacity-100 ${
                      isMobile ? 'opacity-60' : 'opacity-0 group-hover:opacity-100'
                    } ${isDark ? 'bg-gray-700 text-gray-300 hover:text-white' : 'bg-gray-100 text-gray-500 hover:text-gray-900'}`}
                  >
                    {isPinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                  </span>
                  </div>
                );
              })}
            </div>
            )}
          </div>
          </div>
        ))}
      </div>

      {/* Footer — exit button */}
      {onExitWarehouseMode && (
        <div className={`px-3 py-2 border-t ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <button
            onClick={onExitWarehouseMode}
            className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors ${
              isDark
                ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Torna al menu principale
          </button>
        </div>
      )}
    </div>
  );
}
